import * as React from 'react';
import { ContractTag, Team, Player } from '../../../sdk/model';
import { Box, Typography, TableContainer, Paper, Table, TableHead, TableRow, TableBody, Card, CardContent, Chip, useMediaQuery } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import PlayerCardLink from '../../cards/playerCardLink';
import { toSalary } from '../../util/SalaryDisplay';
import TeamDisplay from '../team/TeamDisplay';

interface contractTagDisplayProps {
  contractTag: ContractTag,
  team: Team,
  leagueId: string,
  playerMap: Map<string | undefined, Player | undefined>
}

function ContractTagDisplay(props: contractTagDisplayProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.common.black,
      color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
  }));
  
  function buildTableBody() {
    return <TableBody>
      {props.contractTag.contractTags.map(tag => {
        const player = props.playerMap.get(tag.playerId)!;
        const getTagColor = () => {
          switch(tag.tagType?.toLowerCase()) {
            case 'franchise': return 'error';
            case 'transition': return 'warning';
            case 'rookie': return 'success';
            default: return 'secondary';
          }
        };
        
        return <TableRow key={tag.playerId} sx={{ '&:hover': { backgroundColor: theme.palette.secondary.light + '08' } }}>
          <StyledTableCell>
            <Chip 
              label={tag.tagType} 
              color={getTagColor()} 
              size="small"
              variant="filled"
            />
          </StyledTableCell>
          <StyledTableCell>
            <Typography variant="subtitle2" fontWeight="bold">
              <PlayerCardLink player={player} leagueId={props.contractTag.leagueId} />
            </Typography>
          </StyledTableCell>
          <StyledTableCell>
            <Chip 
              label={toSalary(tag.price)} 
              color="primary" 
              size="small"
              variant="outlined"
            />
          </StyledTableCell>
        </TableRow>
      })}
    </TableBody>
  }
  
  const tableBody = React.useMemo(
      () => buildTableBody(),
      []
    );
    
  if (isMobile) {
    return (
      <Box>
        <Typography variant="body1" sx={{ mb: 2 }}>
          <TeamDisplay leagueId={props.leagueId} team={props.team} emphasized={false} teamId={undefined} />
          {" finalized the following player tags:"}
        </Typography>
        {props.contractTag.contractTags.map(tag => {
          const player = props.playerMap.get(tag.playerId)!;
          return (
            <Card key={tag.playerId} sx={{ mb: 1, borderLeft: 4, borderLeftColor: theme.palette.secondary.main }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                  <Box>
                    <Typography variant="subtitle2" color="primary">
                      <PlayerCardLink player={player} leagueId={props.contractTag.leagueId} />
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Chip label={tag.tagType} color="secondary" size="small" />
                    <Chip label={toSalary(tag.price)} color="primary" size="small" />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    );
  }
  
  // Desktop layout - Enhanced with chips and better styling
  return <Box>
    <Typography variant="body1" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
      <TeamDisplay leagueId={props.leagueId} team={props.team} emphasized={false} teamId={undefined} />
      {" finalized the following player tags:"}
    </Typography>
    <TableContainer component={Paper} sx={{ borderLeft: 4, borderLeftColor: theme.palette.secondary.main }}>
      <Table sx={{ minWidth: 650 }} aria-label="simple table">
        <TableHead sx={{fontSize : 20}}>
          <TableRow>
            <StyledTableCell>Tag Type</StyledTableCell>
            <StyledTableCell>Player</StyledTableCell>
            <StyledTableCell>Price</StyledTableCell>
          </TableRow>
        </TableHead>
        {tableBody}
      </Table>
    </TableContainer>
  </Box>
}

export default ContractTagDisplay;
